import type { ContactSummary, DuplicateGroup } from '@performance-crm/shared';
import { AlertTriangle, GitMerge, Paperclip, ShieldOff } from 'lucide-react';
import { useState } from 'react';
import { LeadSummaryLine, MergeContactsDialog } from '@/components/leads/MergeContactsDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { fmt } from '@/lib/format';
import { useDuplicates } from '@/lib/queries';

/** Proyectos en los que dos o más contactos del grupo tienen lead activo: al fusionar, esos leads se unen. */
function sharedActiveProjects(contacts: ContactSummary[]): string[] {
  const owners = new Map<string, { name: string; contacts: Set<string> }>();
  for (const c of contacts)
    for (const l of c.leads) {
      if (l.status !== 'ACTIVO') continue;
      const entry = owners.get(l.projectId) ?? { name: l.projectName, contacts: new Set() };
      entry.contacts.add(c.id);
      owners.set(l.projectId, entry);
    }
  return [...owners.values()].filter((o) => o.contacts.size > 1).map((o) => o.name);
}

export function DuplicatesTab({ companyId }: { companyId: string }) {
  const { data, isLoading } = useDuplicates(companyId);
  const [target, setTarget] = useState<Record<string, string>>({});
  const [merging, setMerging] = useState<{ target: string; sources: string[] } | null>(null);
  const keptOf = (g: DuplicateGroup) => target[`${g.matchedOn}-${g.key}`] ?? g.contacts[0]!.id;
  return (
    <div className="space-y-3">
      <Card>
        <CardHeader>
          <CardTitle>Posibles duplicados</CardTitle>
          <CardDescription>
            Contactos de la empresa que comparten teléfono, correo o documento. Elige cuál conservar
            y fusiona: los leads, eventos, conversaciones y archivos pasan al conservado; los datos
            distintos (otro celular, correo o documento) quedan como alternos y la identidad los
            sigue encontrando; si ambos tienen lead activo en el mismo proyecto, se unen en uno. Si
            alguno tiene «no contactar», el conservado lo mantiene. La fusión no se puede deshacer:
            se confirma escribiendo «fusionar».
          </CardDescription>
        </CardHeader>
      </Card>
      {isLoading && <Skeleton className="h-40 w-full" />}
      {data?.groups.length === 0 && (
        <p className="text-muted-foreground text-sm">No hay contactos duplicados. 🎉</p>
      )}
      {data?.hasMore && (
        <p className="text-muted-foreground text-sm" data-testid="dup-more">
          Se muestran {data.groups.length} de {data.total} grupos. Fusiona estos y vuelve a cargar
          para ver los siguientes.
        </p>
      )}
      {data?.groups.map((g) => {
        const groupKey = `${g.matchedOn}-${g.key}`;
        const kept = keptOf(g);
        const shared = sharedActiveProjects(g.contacts);
        return (
          <Card key={groupKey} data-testid="dup-group">
            <CardContent className="space-y-2 p-4">
              <p className="text-sm">
                <Badge tone="outline">{g.matchedOn.toLowerCase()}</Badge>{' '}
                <span className="font-medium">{g.key}</span> · {g.contacts.length} contactos
              </p>
              <ul className="space-y-1">
                {g.contacts.map((c) => (
                  <li key={c.id} className="rounded-md border p-2 text-sm">
                    <div className="flex flex-wrap items-center gap-3">
                      <input
                        type="radio"
                        name={`keep-${groupKey}`}
                        checked={kept === c.id}
                        onChange={() => setTarget((t) => ({ ...t, [groupKey]: c.id }))}
                        aria-label={`Conservar ${c.name}`}
                      />
                      <span className="font-medium">{c.name}</span>
                      {c.doNotContact && (
                        <Badge tone="danger" data-testid="dup-dnc">
                          <ShieldOff className="h-3 w-3" /> No contactar
                        </Badge>
                      )}
                      <span className="text-muted-foreground">
                        {c.phone ?? '—'} · {c.email ?? '—'} · {c.documentNumber ?? '—'}
                      </span>
                      <span className="text-muted-foreground ml-auto inline-flex items-center gap-2 text-xs">
                        {c.attachments > 0 && (
                          <span className="inline-flex items-center gap-0.5">
                            <Paperclip className="h-3 w-3" /> {c.attachments}
                          </span>
                        )}
                        {c.leads.length} lead{c.leads.length === 1 ? '' : 's'} · creado{' '}
                        {fmt.date(c.createdAt)}
                      </span>
                    </div>
                    {c.leads.length > 0 && (
                      <ul className="mt-1 space-y-0.5 pl-6 text-xs" data-testid="dup-leads">
                        {c.leads.map((l) => (
                          <li key={l.id}>
                            <LeadSummaryLine lead={l} />
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
              {shared.length > 0 && (
                <p className="text-warning flex items-start gap-2 text-sm" data-testid="dup-join">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  Ambos tienen lead activo en {shared.join(', ')}: se unirán.
                </p>
              )}
              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={() =>
                    setMerging({
                      target: kept,
                      sources: g.contacts.map((c) => c.id).filter((id) => id !== kept),
                    })
                  }
                  data-testid="dup-merge"
                >
                  <GitMerge className="h-4 w-4" /> Fusionar en el conservado
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
      {merging && (
        <MergeContactsDialog
          open
          targetContactId={merging.target}
          sourceContactIds={merging.sources}
          onOpenChange={(open) => !open && setMerging(null)}
        />
      )}
    </div>
  );
}
